var _ = require("lodash");

var { StringId, TypedArrayId, ObjectId, FormatTypeId, typeUnionId, UnionId, NumberId, Identification } = require("../typing/typing");

var replaceFormatedString = require("./replaceFormatedString");
var SE = require("./comp/StatefulExpr");

/**
 * converts string path to an array.
 * '/' is the separator
 * @example
 * stringPath("a/b/c") == ["a", "b", "c"]
 * @param {string} path
 * @return an array of strings
 */
function stringPath(path) {
  path = StringId(path).split('/');
  return path.map(function(p) { return StringId(p).trim(); });
}

/**
 * RefText represents an algorithm to construct a string.
 * There are the following types of RefText objects:
 * - build-in strings - a quoted string.
 * - RefText - a basic constructor representing a string storing
 *   in a database object
 * - FormatedText - a text with placeholders replaced with another texts.
 * @see module:RefText
 * @typedef RefText
 * @global
 */

/**
 * Basic constructor of RefText object.
 * It represents a text given by its path within a text database.
 * A text database object is specified when RefText is resolved.
 * @param {string|string[]} path
 * @param {string|string[]} [prefix]
 * @return {RefText}
 * @see module:RefText~text
 */
function RefText(path, prefix) {
  if(_.keys(this).length > 0) return new RefText(path, prefix);
  this.type = "RefText";
  if(_.isString(path)) path = stringPath(path);
    else TypedArrayId(StringId)(path);
  if(prefix === undefined) this.path = path;
  else {
    if(_.isString(prefix)) prefix = stringPath(prefix);
      else TypedArrayId(StringId)(prefix);
    this.path = _.concat(prefix, path);
  }
  if(this.path === undefined) throw "path undefined";
}

/**
 * Formated text represents a text with variables `"${var}"`
 *   whose values are given in `rules[var]`.
 * @param {RefText} text - text object
 * @param {Object} rules - an object whose values are text objects
 * @return {RefText} FormatedText object
 */
function FormatedText(text, rules) {
  if(_.keys(this).length > 0) return new FormatedText(text, rules);
  this.text  = text;
  this.rules = rules;
  this.type  = "FormatedText";
}

/**
 * @param {RefText} ref - RefText
 * @param {Object} state - if state.path is defined, it is used as a prefix
 * @return RefText object with path being a normalized full path to the text
 */
function makeRef(ref, state) {
  if(_.isString(ref) || _.isNumber(ref)) return ref;
  if(ref.type == "FormatedText")
    return new FormatedText(
        makeRef(ref.text, state),
        _.mapValues(ref.rules, function(t) { return makeRef(t, state); })
      );
  if(SE.isStatefulExpr(ref)) return SE.evaluate(ref, state);
  if(ref.type !== "RefText")
    throw { msg: "Unknown object", ref: ref };
  if(ref.path[0] == "/") {
    var fullPath = _.tail(ref.path);
  } else {
    if(state.textPath === undefined) {
      var fullPath = ref.path;
    } else {
      var fullPath = _.concat(state.textPath, ref.path);
    }
  }
  return new RefText(normalize(fullPath));
};

/**
 * normalize a full path by removing '..' with the following element
 * @param {string[]} fullPath
 * @return an array of strings
 */
function normalize(fullPath) {
  while(_.includes(fullPath, "..")) {
    if(_.indexOf(fullPath, "..") == 0) console.warn("Nothing to remove");
    else {
      // remove ".." and the element before it
     fullPath.splice(_.indexOf(fullPath, "..") - 1, 2);
    }
  }
  return fullPath;
};

/**
 * resolve a RefText object.
 * If text object is not given,
 *   {@link module:RefText~text default object} is used.
 * @param {RefText} ref
 * @param {Object} [text] - an object to search the text in
 * @return string
 */
function resolve(ref, text) {
  if(_.isString(ref) || _.isNumber(ref)) return ref;
  if(!text)
    text = (typeof RefText.text == "function") ? RefText.text() : RefText.text;
    
  if(ref.type == "FormatedText") {
    var rules = _.mapValues(ref.rules,
      function(t) { return resolve(t, text); });
    var str = resolve(ref.text, text);
    return replaceFormatedString(str, rules);
  }
  
  var fullPath = ref.path;
  
  function writeErrorMessageToConsole() {
    console.error("Cannot resole RefText. Observe an information");
    console.warn(ref);
    console.warn(_.cloneDeep(text));
    console.log("That text object does not have " + fullPath.join("."));
    for(var i = 0; i < fullPath.length; ++i) {
      if(! _.has(text, _.take(fullPath, i))) break;
    }
    var fpath = ["text"].concat(_.take(fullPath, i-1)).join(".");
    console.warn("Failed on " + fpath);
  }
  if(!_.has(text, fullPath)) {
    writeErrorMessageToConsole();
    throw "Cannot resolve RefText with full path " + fullPath.join(".");
  }
  return _.get(text, fullPath);
}

/**
 * returns true iff obj is kind of RefText object.
 * @param {*} obj - any JS object
 * @return {boolean}
 */
function isText(obj) {
  if(!obj) return false;
  if(typeof obj == "string") return true;
  var type = obj.type;
  return type == "RefText" || type == "FormatedText";
}

function FormatedTextId(obj) {
  if(ObjectId(obj).type != "FormatedText") throw "type is not FormatedText";
  FormatTypeId({
    text: RefTextId,
    rules: ObjectId
  })(obj);
  _.mapValues(obj.rules, typeUnionId(NumberId, RefTextId));
  return obj;
}
FormatedTextId.typeName = "FormatedText";
FormatedTextId = Identification(FormatedTextId);

/**
 * RefTextId.
 */
var RefTextId = UnionId([
    StringId,
    FormatTypeId({ path: TypedArrayId(StringId) }, "RefText"),
    FormatedTextId
  ], "Text");

// EXPORT

RefText.normalize = normalize;
RefText.makeRef = makeRef;
RefText.resolve = resolve;
RefText.isText = isText;
RefText.TextId = RefTextId;
RefText.RefTextId = RefText.id = RefTextId;
RefText.FormatedText = FormatedText;
/**
 * default text object.
 * If it is a function, `text()` is used.
 * @name text
 * @memberof module:RefText
 */
RefText.text = function() { return window.text; };

module.exports = RefText;
